import { useEffect, useMemo, useState } from "react";
import { Megaphone, Plus, Eye, MousePointerClick, PlayCircle, Copy, Pencil, Trash2, Loader2, Monitor, Tablet, Smartphone, Video, LayoutGrid } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtDateTime } from "../../lib/api";
import { PageHeader, StatCard, StatusBadge, LoadingRows, TableShell, TR, TD, EmptyState } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const AD_TYPES = [["banner_iframe", "Banner (iframe)"], ["responsive_script", "Responsive Network Script"], ["network_script", "Network Script (global)"], ["direct_link", "Direct Link / Smartlink"], ["video", "Video Ad (future provider)"]];
const DEVICES = ["desktop", "tablet", "mobile"];
const PLACEMENTS = [["top", "Top / Header"], ["after_hero", "After Hero"], ["in_content", "In-Content"], ["sidebar", "Sidebar"], ["footer", "Footer"], ["global", "Global (floating)"], ["preroll", "Video Pre-Roll"]];
const PAGE_TYPES = ["home", "shop", "product", "blog", "watch", "other"];
const WEBSITE_TYPES = ["ecommerce", "business", "blog", "creator", "portfolio", "landing"];
const SLOTS = [["top", "Slot 1 — Top / Header"], ["after_hero", "Slot 2 — After Hero / First Content"], ["in_content", "Slot 3 — In-Content"], ["sidebar", "Slot 4 — Sidebar"], ["footer", "Slot 5 — Footer / After Content"]];

const EMPTY_AD = { name: "", network: "", ad_type: "banner_iframe", key: "", script_url: "", full_code: "", container_id: "", sub_kind: "",
  size: { w: 300, h: 250 }, devices: [...DEVICES], placements: [], page_types: [], website_types: [],
  priority: 5, status: "draft", start_date: "", end_date: "", notes: "" };

const previewDoc = (ad) => ad.ad_type === "banner_iframe"
  ? `<!doctype html><html><head><style>html,body{margin:0;overflow:hidden}</style></head><body>
<script>atOptions = { 'key': '${ad.key}', 'format': 'iframe', 'height': ${ad.size?.h || 250}, 'width': ${ad.size?.w || 300}, 'params': {} };</script>
<script src="${ad.script_url}"></script></body></html>`
  : ad.ad_type === "responsive_script"
    ? `<!doctype html><html><body style="margin:0"><div id="${ad.container_id}"></div><script async data-cfasync="false" src="${ad.script_url}"></script></body></html>`
    : ad.ad_type === "network_script"
      ? `<!doctype html><html><body style="margin:0;font-family:sans-serif"><p style="font-size:12px;color:#888;padding:8px">Global network script (${ad.sub_kind || "floating"}) — renders as a floating element on real pages.</p><script async data-cfasync="false" src="${ad.script_url}"></script></body></html>`
      : `<!doctype html><html><body style="margin:0;font-family:sans-serif;display:flex;align-items:center;justify-content:center;min-height:100vh"><a href="${ad.script_url}" target="_blank" rel="noreferrer" style="padding:12px 24px;background:#111;color:#fff;border-radius:999px;text-decoration:none;font-weight:700;font-size:13px">Open Direct Link ↗</a></body></html>`;

function CheckGroup({ label, options, values, onChange, testid }) {
  const opts = options.map((o) => (Array.isArray(o) ? o : [o, o]));
  const toggle = (v) => onChange(values.includes(v) ? values.filter((x) => x !== v) : [...values, v]);
  return (
    <div className="space-y-1.5" data-testid={testid}>
      <Label>{label} <span className="text-muted-foreground font-normal">(empty = all)</span></Label>
      <div className="flex flex-wrap gap-1.5">
        {opts.map(([v, l]) => (
          <button type="button" key={v} onClick={() => toggle(v)} data-testid={`${testid}-${v}`}
            className={`px-3 py-1.5 rounded-full text-[11px] font-extrabold capitalize transition-colors ${values.includes(v) ? "gradient-bg text-white" : "bg-muted text-muted-foreground hover:text-foreground"}`}>
            {l}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function RootAds() {
  const [tab, setTab] = useState("dashboard");
  const [dash, setDash] = useState(null);
  const [settings, setSettings] = useState(null);
  const [codes, setCodes] = useState(null);
  const [codeFilter, setCodeFilter] = useState({ status: "", ad_type: "" });
  const [websites, setWebsites] = useState(null);
  const [wq, setWq] = useState("");
  const [analytics, setAnalytics] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editAd, setEditAd] = useState(null);
  const [previewAd, setPreviewAd] = useState(null);
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [debugQ, setDebugQ] = useState({ slug: "stylehub", page: "home", device: "desktop" });
  const [debugRes, setDebugRes] = useState(null);
  const [adEvents, setAdEvents] = useState(null);

  const loadDash = () => api.get("/root/ads/dashboard").then((r) => { setDash(r.data); setSettings(r.data.settings); }).catch((e) => toast.error(errMsg(e)));
  const loadCodes = () => api.get("/root/ads/codes", { params: { ...Object.fromEntries(Object.entries(codeFilter).filter(([, v]) => v)) } }).then((r) => setCodes(r.data.codes)).catch(() => setCodes([]));
  const loadWebsites = (q = wq) => api.get("/root/ads/websites", { params: q ? { q } : {} }).then((r) => setWebsites(r.data.websites)).catch(() => setWebsites([]));
  const loadAnalytics = () => api.get("/root/ads/analytics", { params: { days: 7 } }).then((r) => setAnalytics(r.data)).catch(() => setAnalytics(null));

  useEffect(() => { loadDash(); }, []);
  useEffect(() => { if (tab === "codes") loadCodes(); }, [tab, codeFilter]); // eslint-disable-line
  useEffect(() => { if (tab === "websites") loadWebsites(""); }, [tab]); // eslint-disable-line
  useEffect(() => { if (tab === "analytics") loadAnalytics(); }, [tab]); // eslint-disable-line
  useEffect(() => { if (tab === "debug") { setAdEvents(null); api.get("/root/ads/events", { params: { limit: 30 } }).then((r) => setAdEvents(r.data.events)).catch(() => setAdEvents([])); } }, [tab]); // eslint-disable-line

  const runDebug = async () => {
    setBusy(true); setDebugRes(null);
    try { const { data } = await api.get("/root/ads/debug", { params: debugQ }); setDebugRes(data); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const saveSettings = async (patch, msg = "Settings saved") => {
    setBusy(true);
    try { const { data } = await api.put("/root/ads/settings", patch); setSettings(data.settings); toast.success(msg); loadDash(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const saveAd = async () => {
    setBusy(true);
    try {
      if (editAd.id) await api.patch(`/root/ads/codes/${editAd.id}`, editAd);
      else await api.post("/root/ads/codes", editAd);
      toast.success(editAd.id ? "Ad updated" : "Ad created");
      setEditAd(null); loadCodes(); loadDash();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const t = dash?.today || {};
  const previewW = previewDevice === "desktop" ? 920 : previewDevice === "tablet" ? 700 : 375;

  return (
    <div className="space-y-2" data-testid="root-ads-page">
      <PageHeader title="Advertisement Management" subtitle="Centralized Smart Ad Engine — all websites, themes & MPlay" testid="root-ads-title" />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="ads-tabs" className="flex-wrap h-auto">
          {["dashboard", "codes", "placements", "websites", "video", "analytics", "debug", "settings"].map((x) => (
            <TabsTrigger key={x} value={x} data-testid={`ads-tab-${x}`} className="capitalize">{x === "codes" ? "Ad Codes" : x === "video" ? "Video Ads" : x}</TabsTrigger>
          ))}
        </TabsList>

        {/* ============ DASHBOARD ============ */}
        <TabsContent value="dashboard" className="mt-6 space-y-6">
          {!dash || !settings ? <LoadingRows rows={3} /> : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[["Global Ads", "global_enabled"], ["Website Ads", "website_ads"], ["Main Site Ads", "main_ads"], ["eCommerce Ads", "ecommerce_ads"], ["Blog Ads", "blog_ads"],
                  ["Business Ads", "business_ads"], ["Creator Ads", "creator_ads"], ["Video Ads", "video_ads"]].map(([l, k], i) => (
                  <div key={k} className="bg-card border border-border rounded-2xl p-4 flex items-center justify-between" data-testid={`dash-toggle-${k}`}>
                    <p className={`font-bold text-sm ${k === "global_enabled" ? "text-primary" : ""}`}>{l}</p>
                    <Switch data-testid={`dash-switch-${k}`} checked={!!settings[k]} disabled={busy}
                      onCheckedChange={(v) => saveSettings({ [k]: v }, `${l} ${v ? "enabled" : "disabled"}`)} />
                  </div>
                ))}
              </div>

              {/* ===== GLOBAL AD EXPERIENCE CONTROL ===== */}
              <div className="bg-card border border-border rounded-2xl p-5 space-y-4" data-testid="experience-panel">
                <p className="font-display font-bold">Global Ad Experience Control</p>
                <div className={`flex items-center justify-between rounded-xl border p-4 ${settings.paused_all ? "border-red-500/60 bg-red-500/10" : "border-border"}`}>
                  <div>
                    <p className="font-bold text-sm text-red-500">PAUSE ALL ADS (emergency)</p>
                    <p className="text-xs text-muted-foreground">Instantly disables every MILTROO-managed ad across all websites & MPlay</p>
                  </div>
                  <Switch data-testid="pause-all-ads" checked={!!settings.paused_all} disabled={busy}
                    onCheckedChange={(v) => saveSettings({ paused_all: v }, v ? "ALL ADS PAUSED globally" : "Ads resumed")} />
                </div>
                <div className="grid sm:grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label>Ad Experience Mode</Label>
                    <Select value={settings.experience_mode || "safe"} onValueChange={(v) => saveSettings({ experience_mode: v }, `Experience mode: ${v.toUpperCase()}`)}>
                      <SelectTrigger data-testid="experience-mode"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="safe">SAFE — fixed slots only, no floating/global scripts</SelectItem>
                        <SelectItem value="balanced">BALANCED — slots + social bar</SelectItem>
                        <SelectItem value="high">HIGH — + admin-activated popunder</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Global Ad Density</Label>
                    <Select value={settings.density || "medium"} onValueChange={(v) => saveSettings({ density: v }, `Density: ${v}`)}>
                      <SelectTrigger data-testid="ad-density"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low (max 2 ads/page)</SelectItem>
                        <SelectItem value="medium">Medium (max 3 ads/page)</SelectItem>
                        <SelectItem value="high">High (max 5 ads/page)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Cooldown between ad triggers (sec)</Label>
                    <div className="flex gap-2">
                      <Input data-testid="cooldown-secs" type="number" min="0" max="3600" value={settings.cooldown_seconds ?? 30}
                        onChange={(e) => setSettings({ ...settings, cooldown_seconds: e.target.value })} />
                      <Button variant="outline" data-testid="cooldown-save" disabled={busy} className="rounded-full font-bold shrink-0"
                        onClick={() => saveSettings({ cooldown_seconds: settings.cooldown_seconds })}>Save</Button>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Disable a problematic ad network</Label>
                  {(dash?.networks || []).map((n) => (
                    <div key={n.network} className="flex items-center justify-between rounded-xl border border-border px-4 py-2.5" data-testid={`network-${n.network}`}>
                      <p className="text-sm font-semibold">{n.network} <span className="text-xs text-muted-foreground font-normal">({n.active}/{n.total} active)</span></p>
                      {n.active > 0 ? (
                        <Button size="sm" variant="outline" data-testid={`network-disable-${n.network}`} disabled={busy}
                          className="rounded-full font-bold text-red-500 border-red-500/40"
                          onClick={() => act(async () => { await api.post(`/root/ads/networks/${encodeURIComponent(n.network)}/status`, { status: "paused" }); loadDash(); }, `${n.network} disabled — other networks unaffected`)}>Disable Network</Button>
                      ) : (
                        <Button size="sm" variant="outline" data-testid={`network-enable-${n.network}`} disabled={busy}
                          className="rounded-full font-bold text-emerald-600 border-emerald-500/40"
                          onClick={() => act(async () => { await api.post(`/root/ads/networks/${encodeURIComponent(n.network)}/status`, { status: "active" }); loadDash(); }, `${n.network} re-enabled`)}>Enable</Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={Megaphone} label="Active Ad Codes" value={dash.active_codes} testid="dash-active-codes" tint="bg-emerald-500/10 text-emerald-500" />
                <StatCard icon={LayoutGrid} label="Active Placements" value={dash.active_placements} testid="dash-active-slots" delay={0.05} tint="bg-blue-500/10 text-blue-500" />
                <StatCard icon={Eye} label="Today's Impressions" value={t.impressions ?? 0} testid="dash-impressions" delay={0.1} tint="bg-violet-500/10 text-violet-500" />
                <StatCard icon={MousePointerClick} label="Today's Clicks" value={t.clicks ?? 0} testid="dash-clicks" delay={0.15} tint="bg-amber-500/10 text-amber-500" />
                <StatCard icon={PlayCircle} label="Video Ad Plays" value={t.video_starts ?? 0} testid="dash-video-plays" delay={0.2} tint="bg-rose-500/10 text-rose-500" />
              </div>
              <div className="flex flex-wrap gap-2.5" data-testid="ads-quick-actions">
                {[["Add Ad Code", () => setEditAd({ ...EMPTY_AD })], ["Manage Ads", () => setTab("codes")], ["Placement Rules", () => setTab("placements")],
                  ["Website Settings", () => setTab("websites")], ["Video Ads", () => setTab("video")], ["Analytics", () => setTab("analytics")], ["Global Settings", () => setTab("settings")]].map(([l, fn]) => (
                  <Button key={l} variant="outline" data-testid={`qa-${l.toLowerCase().replace(/\s+/g, "-")}`} onClick={fn} className="rounded-full font-bold">{l}</Button>
                ))}
              </div>
              {!settings.global_enabled && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4 text-sm" data-testid="ads-off-note">
                  <b>Global Advertisements are OFF.</b> No MILTROO-managed ads render anywhere until you enable this switch.
                </div>
              )}
            </>
          )}
        </TabsContent>

        {/* ============ AD CODES ============ */}
        <TabsContent value="codes" className="mt-6 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Select value={codeFilter.status || "all"} onValueChange={(v) => setCodeFilter({ ...codeFilter, status: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="codes-status-filter" className="w-44"><SelectValue /></SelectTrigger>
              <SelectContent>{["all", "active", "paused", "draft", "expired"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={codeFilter.ad_type || "all"} onValueChange={(v) => setCodeFilter({ ...codeFilter, ad_type: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="codes-type-filter" className="w-56"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All types</SelectItem>{AD_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <Button data-testid="add-ad-code" onClick={() => setEditAd({ ...EMPTY_AD })} className="rounded-full font-bold gradient-bg text-white border-0 ml-auto">
              <Plus className="h-4 w-4 mr-1.5" /> Add Ad Code
            </Button>
          </div>
          {!codes ? <LoadingRows rows={3} /> : codes.length === 0 ? <EmptyState title="No ad codes" /> : (
            <TableShell head={["Ad", "Type", "Size", "Devices", "Placements", "Priority", "Status", "Actions"]} testid="ad-codes-table">
              {codes.map((a) => (
                <TR key={a.id} testid={`ad-code-${a.id}`}>
                  <TD><p className="font-semibold text-sm max-w-[200px] truncate">{a.name}</p><p className="text-[10px] text-muted-foreground">{a.network}</p></TD>
                  <TD><span className="text-xs font-bold">{AD_TYPES.find(([v]) => v === a.ad_type)?.[1] || a.ad_type}</span></TD>
                  <TD><span className="text-xs font-mono">{a.size?.w ? `${a.size.w}×${a.size.h}` : "responsive"}</span></TD>
                  <TD><span className="text-[10px] font-bold capitalize">{a.devices.length ? a.devices.join(", ") : "all"}</span></TD>
                  <TD><span className="text-[10px] font-bold capitalize">{a.placements.length ? a.placements.join(", ") : "—"}</span></TD>
                  <TD><span className="font-bold">{a.priority}</span></TD>
                  <TD><StatusBadge status={a.status} /></TD>
                  <TD>
                    <div className="flex gap-1 flex-wrap">
                      <Button size="sm" variant="ghost" data-testid={`ad-preview-${a.id}`} onClick={() => { setPreviewAd(a); setPreviewDevice("desktop"); }} title="Preview"><Eye className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" data-testid={`ad-edit-${a.id}`} onClick={() => setEditAd({ ...a, size: { ...a.size } })} title="Edit"><Pencil className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" data-testid={`ad-duplicate-${a.id}`} disabled={busy} title="Duplicate"
                        onClick={() => act(async () => { await api.post(`/root/ads/codes/${a.id}/duplicate`); loadCodes(); }, "Duplicated as draft")}><Copy className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" data-testid={`ad-toggle-${a.id}`} disabled={busy} title={a.status === "active" ? "Pause" : "Activate"}
                        onClick={() => act(async () => { await api.post(`/root/ads/codes/${a.id}/status`, { status: a.status === "active" ? "paused" : "active" }); loadCodes(); loadDash(); }, a.status === "active" ? "Ad paused" : "Ad activated")}>
                        <PlayCircle className={`h-4 w-4 ${a.status === "active" ? "text-amber-500" : "text-emerald-500"}`} /></Button>
                      <Button size="sm" variant="ghost" data-testid={`ad-delete-${a.id}`} disabled={busy} title="Delete"
                        onClick={() => act(async () => { await api.delete(`/root/ads/codes/${a.id}`); loadCodes(); loadDash(); }, "Ad deleted")}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </div>
                  </TD>
                </TR>
              ))}
            </TableShell>
          )}
        </TabsContent>

        {/* ============ PLACEMENTS ============ */}
        <TabsContent value="placements" className="mt-6">
          {!settings ? <LoadingRows rows={2} /> : (
            <div className="max-w-2xl space-y-5" data-testid="placements-panel">
              <p className="text-sm text-muted-foreground">The Smart Ad Engine picks the best-sized active ad per slot based on device, page type and website type. Only enabled slots can render — never all ads at once.</p>
              {SLOTS.map(([k, l]) => (
                <div key={k} className="flex items-center justify-between rounded-xl border border-border p-4" data-testid={`slot-${k}`}>
                  <p className="font-bold text-sm">{l}</p>
                  <Switch data-testid={`slot-switch-${k}`} checked={!!settings.slots?.[k]} disabled={busy}
                    onCheckedChange={(v) => saveSettings({ slots: { ...settings.slots, [k]: v } }, "Placement updated")} />
                </div>
              ))}
              <div className="rounded-xl border border-border p-4 space-y-4">
                <p className="font-bold text-sm">Frequency Controls</p>
                <div className="grid sm:grid-cols-3 gap-3">
                  <div className="space-y-1.5"><Label>Max ads per page (1–5)</Label>
                    <Input data-testid="set-max-page" type="number" min="1" max="5" value={settings.max_ads_per_page} onChange={(e) => setSettings({ ...settings, max_ads_per_page: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Max ads per session</Label>
                    <Input data-testid="set-max-session" type="number" min="1" max="100" value={settings.max_ads_per_session} onChange={(e) => setSettings({ ...settings, max_ads_per_session: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Per-ad session cap</Label>
                    <Input data-testid="set-per-ad-cap" type="number" min="1" max="50" value={settings.per_ad_session_cap} onChange={(e) => setSettings({ ...settings, per_ad_session_cap: e.target.value })} /></div>
                </div>
                <Button data-testid="save-frequency" disabled={busy}
                  onClick={() => saveSettings({ max_ads_per_page: settings.max_ads_per_page, max_ads_per_session: settings.max_ads_per_session, per_ad_session_cap: settings.per_ad_session_cap })}
                  className="rounded-full font-bold gradient-bg text-white border-0">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Frequency Rules"}</Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ============ WEBSITES ============ */}
        <TabsContent value="websites" className="mt-6 space-y-4">
          <div className="flex gap-2 max-w-md">
            <Input data-testid="ws-search" placeholder="Search website…" value={wq} onChange={(e) => setWq(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadWebsites()} />
            <Button data-testid="ws-search-btn" variant="outline" onClick={() => loadWebsites()} className="rounded-full font-bold">Search</Button>
          </div>
          {!websites ? <LoadingRows rows={3} /> : websites.length === 0 ? <EmptyState title="No websites found" /> : (
            <TableShell head={["Website", "Slug", "Type", "Status", "Advertisements"]} testid="ws-table">
              {websites.map((w) => (
                <TR key={w.id} testid={`ws-row-${w.id}`}>
                  <TD><p className="font-semibold text-sm">{w.name}</p></TD>
                  <TD><span className="text-xs font-mono">/site/{w.slug}</span></TD>
                  <TD><span className="text-xs font-bold capitalize">{w.type}</span></TD>
                  <TD><StatusBadge status={w.status} /></TD>
                  <TD>
                    <Select value={w.ads} onValueChange={(v) => act(async () => { await api.post(`/root/ads/websites/${w.id}`, { ads: v }); loadWebsites(); }, `Ads ${v === "off" ? "disabled" : v === "on" ? "enabled" : "set to inherit"} for ${w.name}`)}>
                      <SelectTrigger data-testid={`ws-ads-${w.id}`} className="w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="inherit">Inherit (ON)</SelectItem>
                        <SelectItem value="on">ON</SelectItem>
                        <SelectItem value="off">OFF</SelectItem>
                      </SelectContent>
                    </Select>
                  </TD>
                </TR>
              ))}
            </TableShell>
          )}
        </TabsContent>

        {/* ============ VIDEO ADS ============ */}
        <TabsContent value="video" className="mt-6">
          {!settings ? <LoadingRows rows={2} /> : (
            <div className="max-w-2xl space-y-5" data-testid="video-ads-panel">
              <div className="rounded-xl border border-border p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div><p className="font-bold text-sm flex items-center gap-2"><Video className="h-4 w-4 text-primary" /> Video Pre-Roll</p>
                    <p className="text-xs text-muted-foreground">On MPlay watch pages, an ad overlay appears before playback. Never injected inside YouTube/Instagram/Facebook players.</p></div>
                  <Switch data-testid="preroll-enable" checked={!!settings.preroll?.enabled} disabled={busy}
                    onCheckedChange={(v) => saveSettings({ preroll: { ...settings.preroll, enabled: v } }, v ? "Pre-roll enabled" : "Pre-roll disabled")} />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5"><Label>Skip delay (seconds)</Label>
                    <Input data-testid="preroll-skip" type="number" min="0" max="30" value={settings.preroll?.skip_delay ?? 5} onChange={(e) => setSettings({ ...settings, preroll: { ...settings.preroll, skip_delay: e.target.value } })} /></div>
                  <div className="space-y-1.5"><Label>Max ad duration (seconds)</Label>
                    <Input data-testid="preroll-max" type="number" min="5" max="60" value={settings.preroll?.max_duration ?? 20} onChange={(e) => setSettings({ ...settings, preroll: { ...settings.preroll, max_duration: e.target.value } })} /></div>
                  <div className="space-y-1.5"><Label>Pre-roll cooldown (minutes)</Label>
                    <Input data-testid="preroll-cooldown" type="number" min="0" max="1440" value={settings.preroll_cooldown_minutes ?? 10} onChange={(e) => setSettings({ ...settings, preroll_cooldown_minutes: e.target.value })} /></div>
                </div>
                <p className="text-[11px] text-muted-foreground">Pre-roll shows at most once per cooldown window per viewer — repeated Play clicks never keep triggering ads.</p>
                <Button data-testid="preroll-save" disabled={busy}
                  onClick={() => saveSettings({ preroll: settings.preroll, preroll_cooldown_minutes: settings.preroll_cooldown_minutes })}
                  className="rounded-full font-bold gradient-bg text-white border-0">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Pre-Roll Settings"}</Button>
              </div>
              <div className="rounded-xl border border-border p-4 space-y-3">
                <p className="font-bold text-sm">Mid-Roll (long videos)</p>
                <Select value={settings.midroll?.mode || "off"} onValueChange={(v) => saveSettings({ midroll: { mode: v } }, "Mid-roll updated")}>
                  <SelectTrigger data-testid="midroll-mode" className="w-56"><SelectValue /></SelectTrigger>
                  <SelectContent>{[["off", "OFF"], ["5m", "Every 5 minutes"], ["10m", "Every 10 minutes"], ["25p", "At 25%"], ["50p", "At 50%"], ["75p", "At 75%"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">Mid-roll applies to MILTROO self-hosted (HTML5) videos only — third-party embedded players cannot be intercepted. Short videos never get mid-roll.</p>
              </div>
              <div className="rounded-xl border border-dashed border-border p-4" data-testid="video-provider-note">
                <p className="font-bold text-sm">Video Ad Provider architecture</p>
                <p className="text-xs text-muted-foreground mt-1">The Ad Code Library supports a dedicated <b>video</b> ad type (VAST / VPAID / MP4 / HTML5). Add a video ad code here later and it plugs into the pre-roll/mid-roll engine without any theme changes. Current banner scripts are served as display pre-roll overlays — clearly labeled “Advertisement”, never mislabeled as skippable video creatives.</p>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ============ ANALYTICS ============ */}
        <TabsContent value="analytics" className="mt-6 space-y-6">
          {!analytics ? <LoadingRows rows={3} /> : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={PlayCircle} label="Video Starts (7d)" value={analytics.video.ad_start} testid="an-vstart" tint="bg-emerald-500/10 text-emerald-500" />
                <StatCard icon={PlayCircle} label="Video Completes (7d)" value={analytics.video.ad_complete} testid="an-vcomplete" delay={0.05} tint="bg-blue-500/10 text-blue-500" />
                <StatCard icon={PlayCircle} label="Video Skips (7d)" value={analytics.video.ad_skip} testid="an-vskip" delay={0.1} tint="bg-amber-500/10 text-amber-500" />
                <StatCard icon={PlayCircle} label="Skip Rate" value={`${analytics.video.skip_rate}%`} testid="an-skiprate" delay={0.15} tint="bg-rose-500/10 text-rose-500" />
              </div>
              <div>
                <h3 className="font-display font-bold text-sm mb-2">Daily (real events)</h3>
                {analytics.by_day.length === 0 ? <EmptyState title="No events yet" body="Impressions and clicks are recorded as real visitors see ads." /> : (
                  <TableShell head={["Date", "Impressions", "Clicks", "CTR"]} testid="an-day-table">
                    {analytics.by_day.map((d) => (
                      <TR key={d.day} testid={`an-day-${d.day}`}>
                        <TD><span className="font-semibold text-sm">{d.day}</span></TD>
                        <TD><span className="font-bold">{d.impressions}</span></TD>
                        <TD><span className="font-bold">{d.clicks}</span></TD>
                        <TD><span className="text-xs font-bold">{d.impressions ? `${(d.clicks / d.impressions * 100).toFixed(1)}%` : "—"}</span></TD>
                      </TR>
                    ))}
                  </TableShell>
                )}
              </div>
              <div>
                <h3 className="font-display font-bold text-sm mb-2">Per Ad</h3>
                {analytics.per_ad.length === 0 ? <EmptyState title="No per-ad data yet" /> : (
                  <TableShell head={["Ad", "Impressions", "Clicks"]} testid="an-ad-table">
                    {analytics.per_ad.map((a) => (
                      <TR key={a.ad_id} testid={`an-ad-${a.ad_id}`}>
                        <TD><span className="font-semibold text-sm">{a.name}</span></TD>
                        <TD><span className="font-bold">{a.impressions}</span></TD>
                        <TD><span className="font-bold">{a.clicks}</span></TD>
                      </TR>
                    ))}
                  </TableShell>
                )}
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-card border border-border rounded-2xl p-4" data-testid="an-by-device">
                  <p className="font-bold text-sm mb-2">Impressions by Device</p>
                  {Object.keys(analytics.by_device).length === 0 ? <p className="text-xs text-muted-foreground">No data</p> :
                    Object.entries(analytics.by_device).map(([k, v]) => <p key={k} className="text-xs font-bold capitalize">{k}: {v}</p>)}
                </div>
                <div className="bg-card border border-border rounded-2xl p-4" data-testid="an-by-placement">
                  <p className="font-bold text-sm mb-2">Impressions by Placement</p>
                  {Object.keys(analytics.by_placement).length === 0 ? <p className="text-xs text-muted-foreground">No data</p> :
                    Object.entries(analytics.by_placement).map(([k, v]) => <p key={k} className="text-xs font-bold capitalize">{k}: {v}</p>)}
                </div>
              </div>
            </>
          )}
        </TabsContent>

        {/* ============ SETTINGS ============ */}
        <TabsContent value="settings" className="mt-6">
          {!settings ? <LoadingRows rows={2} /> : (
            <div className="max-w-lg space-y-4" data-testid="ads-settings-panel">
              <p className="text-sm text-muted-foreground">Category master switches — each applies only when Global Ads is ON. Per-website overrides are in the Websites tab.</p>
              {[["website_ads", "Website Advertisements (all tenant sites)"], ["main_ads", "MILTROO Main Website Ads"], ["ecommerce_ads", "eCommerce Advertisements"], ["blog_ads", "Blog Advertisements"],
                ["business_ads", "Business Advertisements"], ["creator_ads", "Creator (MPlay) Advertisements"], ["video_ads", "Video Advertisements (pre-roll on watch pages)"]].map(([k, l]) => (
                <div key={k} className="flex items-center justify-between rounded-xl border border-border p-4" data-testid={`setting-${k}`}>
                  <p className="font-bold text-sm">{l}</p>
                  <Switch data-testid={`setting-switch-${k}`} checked={!!settings[k]} disabled={busy}
                    onCheckedChange={(v) => saveSettings({ [k]: v }, "Updated")} />
                </div>
              ))}
            </div>
          )}
        </TabsContent>
        {/* ============ DEBUG ============ */}
        <TabsContent value="debug" className="mt-6 space-y-5" data-testid="debug-tab">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="space-y-1.5"><Label>Slug <span className="text-muted-foreground font-normal">(empty = MILTROO main)</span></Label>
              <Input data-testid="debug-slug" className="w-52" placeholder="stylehub / mplay" value={debugQ.slug} onChange={(e) => setDebugQ({ ...debugQ, slug: e.target.value.trim() })} /></div>
            <div className="space-y-1.5"><Label>Page Type</Label>
              <Select value={debugQ.page} onValueChange={(v) => setDebugQ({ ...debugQ, page: v })}>
                <SelectTrigger data-testid="debug-page" className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>{PAGE_TYPES.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Device</Label>
              <Select value={debugQ.device} onValueChange={(v) => setDebugQ({ ...debugQ, device: v })}>
                <SelectTrigger data-testid="debug-device" className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>{DEVICES.map((d) => <SelectItem key={d} value={d} className="capitalize">{d}</SelectItem>)}</SelectContent>
              </Select></div>
            <Button data-testid="debug-run" onClick={runDebug} disabled={busy} className="rounded-full font-bold gradient-bg text-white border-0">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simulate Rules"}</Button>
          </div>
          {debugRes && (
            <div className="space-y-4" data-testid="debug-result">
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                {[["Scope", debugRes.scope], ["Website Type", debugRes.website_type], ["Ads Enabled", debugRes.config_enabled ? "YES" : "NO"],
                  ["Slots Served", debugRes.slots_served.join(", ") || "none"], ["Pre-Roll", debugRes.preroll_served ? "YES" : "no"]].map(([l, v]) => (
                  <div key={l} className="bg-card border border-border rounded-xl p-3" data-testid={`debug-${l.toLowerCase().replace(/\s+/g, "-")}`}>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{l}</p>
                    <p className="font-bold text-sm mt-1 capitalize">{v}</p>
                  </div>
                ))}
              </div>
              {debugRes.gates.length > 0 && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-sm space-y-1" data-testid="debug-gates">
                  {debugRes.gates.map((g) => <p key={g}>⚠ {g}</p>)}
                </div>
              )}
              <TableShell head={["Ad", "Type", "Size", "Status", "Matched Rule / Exclusion Reason"]} testid="debug-decisions-table">
                {debugRes.decisions.map((d) => (
                  <TR key={d.id} testid={`debug-ad-${d.id}`}>
                    <TD><span className="font-semibold text-sm">{d.name}</span></TD>
                    <TD><span className="text-xs">{d.ad_type}</span></TD>
                    <TD><span className="text-xs font-mono">{d.size?.w ? `${d.size.w}×${d.size.h}` : "resp."}</span></TD>
                    <TD><StatusBadge status={d.status} /></TD>
                    <TD><span className={`text-xs font-semibold ${d.decision.startsWith("matched") ? "text-emerald-600" : "text-muted-foreground"}`}>{d.decision}</span></TD>
                  </TR>
                ))}
              </TableShell>
            </div>
          )}
          <div>
            <h3 className="font-display font-bold text-sm mb-2">Recent Ad Events (real render telemetry)</h3>
            {!adEvents ? <LoadingRows rows={2} /> : adEvents.length === 0 ? <EmptyState title="No events yet" body="render_ok / render_error / impression events appear here as real pages render ads." /> : (
              <TableShell head={["Event", "Placement", "Page", "Device", "Container", "Error", "Time"]} testid="debug-events-table">
                {adEvents.map((e) => (
                  <TR key={e.id} testid={`debug-event-${e.id}`}>
                    <TD><span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full ${e.event === "render_error" ? "bg-red-500/10 text-red-500" : e.event === "render_ok" ? "bg-emerald-500/10 text-emerald-600" : "bg-blue-500/10 text-blue-500"}`}>{e.event}</span></TD>
                    <TD><span className="text-xs font-bold">{e.placement || "—"}</span></TD>
                    <TD><span className="text-xs">{e.slug || "main"}/{e.page}</span></TD>
                    <TD><span className="text-xs capitalize">{e.device}</span></TD>
                    <TD><span className="text-xs font-mono">{e.container || "—"}</span></TD>
                    <TD><span className="text-[10px] text-red-500 max-w-[160px] truncate block">{e.error || ""}</span></TD>
                    <TD><span className="text-[10px] text-muted-foreground">{fmtDateTime(e.created_at)}</span></TD>
                  </TR>
                ))}
              </TableShell>
            )}
          </div>
        </TabsContent>

      </Tabs>

      {/* ============ EDIT / CREATE DIALOG ============ */}
      <Dialog open={!!editAd} onOpenChange={(o) => !o && setEditAd(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ad-editor">
          {editAd && (
            <>
              <DialogHeader><DialogTitle>{editAd.id ? "Edit Ad Code" : "Add Ad Code"}</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5"><Label>Ad Name *</Label>
                    <Input data-testid="ad-name" value={editAd.name} onChange={(e) => setEditAd({ ...editAd, name: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Ad Network</Label>
                    <Input data-testid="ad-network" value={editAd.network} onChange={(e) => setEditAd({ ...editAd, network: e.target.value })} placeholder="HighRevenueFormat / AdSense / …" /></div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5"><Label>Ad Type</Label>
                    <Select value={editAd.ad_type} onValueChange={(v) => setEditAd({ ...editAd, ad_type: v })}>
                      <SelectTrigger data-testid="ad-type"><SelectValue /></SelectTrigger>
                      <SelectContent>{AD_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div className="space-y-1.5"><Label>Status</Label>
                    <Select value={editAd.status} onValueChange={(v) => setEditAd({ ...editAd, status: v })}>
                      <SelectTrigger data-testid="ad-status"><SelectValue /></SelectTrigger>
                      <SelectContent>{["active", "paused", "draft", "expired"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                    </Select></div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5"><Label>Key</Label>
                    <Input data-testid="ad-key" value={editAd.key} onChange={(e) => setEditAd({ ...editAd, key: e.target.value })} className="font-mono text-xs" /></div>
                  <div className="space-y-1.5"><Label>Script / URL</Label>
                    <Input data-testid="ad-script" value={editAd.script_url} onChange={(e) => setEditAd({ ...editAd, script_url: e.target.value })} className="font-mono text-xs" /></div>
                </div>
                <div className="space-y-1.5"><Label>Full Ad Code (exact snippet — stored &amp; executed verbatim, never modified)</Label>
                  <Textarea data-testid="ad-fullcode" rows={5} value={editAd.full_code || ""} onChange={(e) => setEditAd({ ...editAd, full_code: e.target.value })}
                    className="font-mono text-[11px]" placeholder="Paste the complete ad code exactly as provided by the network" /></div>
                {editAd.ad_type === "banner_iframe" && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5"><Label>Width (px)</Label>
                      <Input data-testid="ad-w" type="number" min="0" value={editAd.size.w} onChange={(e) => setEditAd({ ...editAd, size: { ...editAd.size, w: e.target.value } })} /></div>
                    <div className="space-y-1.5"><Label>Height (px)</Label>
                      <Input data-testid="ad-h" type="number" min="0" value={editAd.size.h} onChange={(e) => setEditAd({ ...editAd, size: { ...editAd.size, h: e.target.value } })} /></div>
                  </div>
                )}
                {editAd.ad_type === "responsive_script" && (
                  <div className="space-y-1.5"><Label>Container ID</Label>
                    <Input data-testid="ad-container" value={editAd.container_id} onChange={(e) => setEditAd({ ...editAd, container_id: e.target.value })} className="font-mono text-xs" /></div>
                )}
                {editAd.ad_type === "network_script" && (
                  <div className="space-y-1.5"><Label>Network Script Kind</Label>
                    <Select value={editAd.sub_kind || "social_bar"} onValueChange={(v) => setEditAd({ ...editAd, sub_kind: v })}>
                      <SelectTrigger data-testid="ad-subkind"><SelectValue /></SelectTrigger>
                      <SelectContent>{["social_bar", "popunder", "push", "other"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                    </Select></div>
                )}
                <CheckGroup label="Devices" options={DEVICES} values={editAd.devices} onChange={(v) => setEditAd({ ...editAd, devices: v })} testid="ad-devices" />
                <CheckGroup label="Placements" options={PLACEMENTS} values={editAd.placements} onChange={(v) => setEditAd({ ...editAd, placements: v })} testid="ad-placements" />
                <CheckGroup label="Page Types" options={PAGE_TYPES} values={editAd.page_types} onChange={(v) => setEditAd({ ...editAd, page_types: v })} testid="ad-pagetypes" />
                <CheckGroup label="Website Types" options={WEBSITE_TYPES} values={editAd.website_types} onChange={(v) => setEditAd({ ...editAd, website_types: v })} testid="ad-sitetypes" />
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1.5"><Label>Priority (0–100)</Label>
                    <Input data-testid="ad-priority" type="number" min="0" max="100" value={editAd.priority} onChange={(e) => setEditAd({ ...editAd, priority: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Start Date</Label>
                    <Input data-testid="ad-start" type="date" value={editAd.start_date} onChange={(e) => setEditAd({ ...editAd, start_date: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>End Date</Label>
                    <Input data-testid="ad-end" type="date" value={editAd.end_date} onChange={(e) => setEditAd({ ...editAd, end_date: e.target.value })} /></div>
                </div>
                <div className="space-y-1.5"><Label>Notes</Label>
                  <Textarea data-testid="ad-notes" rows={2} value={editAd.notes} onChange={(e) => setEditAd({ ...editAd, notes: e.target.value })} /></div>
                <Button data-testid="ad-save" disabled={busy} onClick={saveAd} className="rounded-full font-bold gradient-bg text-white border-0 w-full">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : editAd.id ? "Save Changes" : "Create Ad Code"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ============ PREVIEW DIALOG (sandboxed) ============ */}
      <Dialog open={!!previewAd} onOpenChange={(o) => !o && setPreviewAd(null)}>
        <DialogContent className="max-w-4xl" data-testid="ad-preview-dialog">
          {previewAd && (
            <>
              <DialogHeader><DialogTitle>Preview: {previewAd.name}</DialogTitle></DialogHeader>
              <div className="flex gap-2 mb-3">
                {[["desktop", Monitor], ["tablet", Tablet], ["mobile", Smartphone]].map(([d, Icon]) => (
                  <Button key={d} size="sm" variant={previewDevice === d ? "default" : "outline"} data-testid={`preview-${d}`}
                    onClick={() => setPreviewDevice(d)} className="rounded-full font-bold capitalize"><Icon className="h-4 w-4 mr-1.5" />{d}</Button>
                ))}
              </div>
              <div className="bg-muted/50 rounded-2xl p-6 overflow-x-auto">
                <div className="mx-auto bg-card border border-border rounded-xl p-4 transition-all" style={{ width: previewW, maxWidth: "100%" }}>
                  <p className="text-[9px] uppercase tracking-widest opacity-40 mb-2">Advertisement · approx. placement</p>
                  <div className="flex justify-center" data-testid="ad-preview-frame-wrap">
                    <iframe key={previewAd.id + previewDevice} title="Ad preview" srcDoc={previewDoc(previewAd)} data-testid="ad-preview-frame"
                      width={previewAd.size?.w || Math.min(previewW - 40, 728)} height={previewAd.size?.h || 250}
                      style={{ maxWidth: "100%", border: 0 }} sandbox="allow-scripts allow-same-origin allow-popups" scrolling="no" frameBorder="0" />
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
